import Vue from 'vue'
import NetworkCommand from '../NetworkCommand'
import { onSnapshot } from 'firebase/firestore'
import { firestoreDoc, snapshotDoc, getStorageFileUrl } from '../../util/firestoreUtil'
import day6Api from '../../api/day6'
import sceneApi from '../../api/sceneContent'
import { day6Analytics } from '../../util/analyticsUtil'
import ShareLevel from '../../constants/Day6ShareLevel'

const MUTA = {
  isQueryingDimensions: 'isQueryingDimensions',
  queryDimensionsError: 'queryDimensionsError',
  isQueryingPose: 'isQueryingPose',
  setSaveDimensions: 'setSaveDimensions',
  isQueuingBuild: 'isQueuingBuild',
  isSavingPose: 'isSavingPose',
  isQueryingPreviewSettings: 'isQueryingPreviewSettings',
  isSavingPreviewSettings: 'isSavingPreviewSettings',
  isSharingCharacter: 'isSharingCharacter',
  queryingShareId: 'queryingShareId',
  queryShareLevelError: 'queryShareLevelError'
}

const snapshotBodyDimensions = (accountPath, dimensionsId) => snapshotDoc(`day6-data/${accountPath}/body-dimensions/${dimensionsId}`)

const getSummaryData = async (accountId, idSummary) => {
  const summarySnap = await snapshotDoc(`day6-data/${accountId}/day6-account-dimension-summaries/${idSummary}`)
  return summarySnap.exists() ? summarySnap.data().exin : {}
}

const characterPosePath = (accountPath, poseId) => {
  const characterPosePath = accountPath === '_' ? 'character-pose' : 'day6-account-character-pose'
  return `day6-data/${accountPath}/${characterPosePath}/${poseId}`
}

const sceneSettingsPath = (accountPath, sceneSettingsId) => `scene-content/${accountPath}/scene-settings/${sceneSettingsId}`

const getCharacterPoseData = async (accountPath, poseId) => {
  const snap = await snapshotDoc(characterPosePath(accountPath, poseId))
  let characterPose, poseBodyDimensions
  if (snap.exists()) {
    ({ characterPose, dimensions: poseBodyDimensions } = snap.data().pose)
  }
  return { characterPose, poseBodyDimensions }
}

const getScenePreviewSettings = async (accountPath, sceneSettingsId) => {
  const snap = await snapshotDoc(sceneSettingsPath(accountPath, sceneSettingsId))
  const previewSettings = snap.get('preview-settings')
  return { previewSettings }
}

const getStorageFileDownloadUrl = async (fileUri) => {
  const url = await getStorageFileUrl(fileUri)
  return url.endsWith('?') ? url.substring(0, url.length - 1) : url
}

const guardLogStorageFileDownloadUrl = async (fileUri) => {
  try {
    return getStorageFileDownloadUrl(fileUri)
  } catch (e) {
    Vue.logger.error(e)
  }
}

const isBlockingRequest = (state, dimensionsId) => state.isQueuingBuild ||
    // Saving pose without dimensions ID will assign one on backend
    (state.isSavingPose && !dimensionsId)

const getShareDimensionsId = async ({ accountId, id }) => {
  try {
    const accessSnap = await snapshotDoc(`day6-access-permission/${id}`)
    if (accessSnap.exists() && accessSnap.get('exin.uid') === accountId) {
      let snap
      let idSwap
      const { dimensionsId } = await day6Api.getShareDimensionsId(id)
      if (dimensionsId) {
        snap = await snapshotBodyDimensions(accountId, dimensionsId)
        if (snap.exists()) {
          idSwap = dimensionsId
        }
      }
      // In this state the share ID should map to a dimensions ID. If dimensions ID is not returned from the backend for any reason the caller should alert of missing dimensions/invalid share ID.
      return idSwap ? { snap, idSwap } : { isMissingDimensions: true }
    }
  } catch (e) {
    // Not out of the ordinary
    Vue.logger.log('Not own share dimensions')
  }
  return {}
}

const querySharedAccountCharacter = async shareId => {
  const {
    bodyDimensions,
    characterPose,
    buildFileUri,
    poseBodyDimensions,
    previewSettings,
    poseSequenceData
  } = await day6Api.getSharedCharacter(shareId)
  let fileDownloadUrl
  if (buildFileUri) {
    fileDownloadUrl = await guardLogStorageFileDownloadUrl(buildFileUri)
  }
  return bodyDimensions
    // Structure must match public share (below)
    ? {
        shared: {
          fileDownloadUrl,
          bodyDimensions,
          characterPose,
          poseBodyDimensions,
          previewSettings,
          poseSequenceData
        }
      }
    : null
}

const getSharedPublicDimensions = async ({ build, dimensions }) => {
  const { bodyDimensions, idPose, idPreviewSettings } = dimensions
  // Opening share link before asset is built may result in undefined build data
  const { buildFileUri } = build || {}
  let fileDownloadUrl
  if (buildFileUri) {
    fileDownloadUrl = await guardLogStorageFileDownloadUrl(buildFileUri)
  }
  let characterPose, poseBodyDimensions
  if (idPose) {
    ({ characterPose, dimensions: poseBodyDimensions } = await getCharacterPoseData('_', idPose))
  }
  let previewSettings
  if (idPreviewSettings) {
    ({ previewSettings } = await getScenePreviewSettings('_', idPreviewSettings))
  }
  // Structure must match account share (above)
  return {
    shared: {
      fileDownloadUrl,
      bodyDimensions,
      characterPose,
      poseBodyDimensions,
      previewSettings
    }
  }
}

// initial state
const initialState = {
  isQueryingDimensions: false,
  queryDimensionsError: undefined,
  isQueryingPose: false,
  saveDimensionIds: {},
  saveDimensionsError: undefined,
  isQueuingBuild: false,
  isSavingPose: false,
  isQueryingPreviewSettings: false,
  isSavingPreviewSettings: false,
  isSharingCharacter: false,
  queryingShareId: '',
  queryShareLevelError: undefined
}

// getters
const moduleGetters = {
  queryDimensionsError: s => s.queryDimensionsError,
  isSavingDimensions: (state) => (id) => id in state.saveDimensionIds,
  saveDimensionsError: s => s.saveDimensionsError,
  queryShareLevelError: s => s.queryShareLevelError
}

// actions
const actions = {
  async queryDefaultDimensions (_, bodyType) {
    const path = `day6-data/default-dimensions/${bodyType}/latest`
    const snap = await snapshotDoc(path)
    const dimensions = snap.data()
    if (dimensions) {
      delete dimensions.version
    }
    return dimensions
  },
  async queryDimensionsData ({ commit, rootGetters }, { id, checkForShare, isPlusDownload }) {
    if (!id) {
      return { }
    }

    const accountId = rootGetters.accountId()
    const isPaid = accountId && rootGetters.isAccountPaid

    return new NetworkCommand(
      commit,
      MUTA.isQueryingDimensions,
      async () => {
        let snap
        // Only paid is able to make changes so don't give false impression if not paid.
        // Not worth it to support accessing paid features and assets when authenticated and free.
        if (isPaid) {
          snap = await snapshotBodyDimensions(accountId, id)
          if (!snap.exists()) {
            snap = null
          }
        }
        if (!snap) {
          snap = await snapshotBodyDimensions('_', id)

          // Existing public dimensions is shared if checking for share
          if (snap.exists() && checkForShare) {
            return getSharedPublicDimensions(snap.data())
          }
        }

        // ID may be share ID from own account. If so swap IDs proper so is properly editable.
        let idSwap
        if (!snap.exists() && isPaid && checkForShare) {
          const { snap: swapSnap, idSwap: swapId, isMissingDimensions } = await getShareDimensionsId({ accountId, id })
          if (swapSnap && swapId) {
            snap = swapSnap
            idSwap = swapId
          } else if (isMissingDimensions) {
            return {}
          }
        }

        // Dimensions does not exist
        if (!snap.exists()) {
          let sharedData
          if (checkForShare) {
            // Try accessing shared if all previous dimensions does not exist
            sharedData = await querySharedAccountCharacter(id)
          }
          return sharedData || {}
        }

        // No build
        const { build, meta, dimensions } = snap.data()
        const { idSummary } = meta
        if (!(build && build.buildFileUri)) {
          return {
            id,
            idSwap,
            idSummary,
            dimensions
          }
        }

        const { idBuildStatus } = meta
        // Default file URL copied on pose of default body will not have a build status
        let isDownloadValid = !idBuildStatus
        if (idBuildStatus) {
          const statusSnap = await snapshotDoc(`job-status/${idBuildStatus}`)
          const { state, exin } = statusSnap.data() || { exin: {} }
          const { idSource, errorMessage } = exin
          const isMatchingId = (idSwap || id) === idSource
          isDownloadValid = isMatchingId && !errorMessage && state === 'end'
        }
        let fileDownloadUrl = ''
        if (isDownloadValid) {
          let buildFileUri = build.buildFileUri

          if (isPlusDownload && rootGetters.isAccountPaid) {
            const dimensionsId = idSwap || id
            const { assetUri } = await day6Api.getCharacterAssetUri(dimensionsId)
            if (assetUri) {
              buildFileUri = assetUri
            }
          }

          fileDownloadUrl = await guardLogStorageFileDownloadUrl(buildFileUri)
        }
        return {
          id,
          idSwap,
          idSummary,
          fileDownloadUrl,
          dimensions
        }
      },
      MUTA.queryDimensionsError
    ).executeAsync()
  },
  async queryLatestDimensions ({ commit, rootGetters }, dimensionsId) {
    const accountId = rootGetters.accountId()
    if (!accountId) { return {} }

    return new NetworkCommand(
      commit,
      MUTA.isQueryingDimensions,
      async () => {
        const dimensionsSnap = await snapshotBodyDimensions(accountId, dimensionsId)
        const { meta } = dimensionsSnap.exists() ? dimensionsSnap.data() : {}
        if (meta) {
          const summary = await getSummaryData(accountId, meta.idSummary)
          const currentId = summary.current
          if (currentId !== dimensionsId) {
            return {
              latestId: currentId,
              ...summary[currentId]
            }
          }
        }
        return {}
      }
    ).executeAsync()
  },
  async queryCharacterPose ({ commit, rootGetters }, id) {
    if (!id) {
      return {}
    }

    return new NetworkCommand(
      commit,
      MUTA.isQueryingPose,
      async () => {
        const accountId = rootGetters.accountId()
        if (accountId) {
          const poseData = await getCharacterPoseData(accountId, id)
          if (poseData.characterPose) {
            return poseData
          }
        }

        return getCharacterPoseData('_', id)
      }
    ).executeAsync()
  },
  async queryPreviewSettings ({ commit, rootGetters }, id) {
    if (!id) { return {} }

    return new NetworkCommand(
      commit,
      MUTA.isQueryingPreviewSettings,
      async () => {
        const accountId = rootGetters.accountId()
        if (accountId) {
          const poseData = await getScenePreviewSettings(accountId, id)
          if (poseData.previewSettings) {
            return poseData
          }
        }

        return getScenePreviewSettings('_', id)
      }
    ).executeAsync()
  },
  async queryDimensionsShareLevel ({ state, commit, rootGetters }, dimensionsId) {
    const accountId = rootGetters.accountId()
    if (!(accountId && rootGetters.isAccountPaid)) { return ShareLevel.PUBLIC }

    const dimensionsSnap = await snapshotBodyDimensions(accountId, dimensionsId)
    const { meta } = dimensionsSnap.exists() ? dimensionsSnap.data() : {}
    if (!meta) { return ShareLevel.PRIVATE }

    return new NetworkCommand(
      commit,
      isLoading => {
        if (isLoading) {
          commit(MUTA.queryingShareId, dimensionsId)
        } else if (state.queryingShareId === dimensionsId) {
          commit(MUTA.queryingShareId, '')
        }
      },
      async () => {
        const { publicShareId, shareLookup } = await getSummaryData(accountId, meta.idSummary)
        if (publicShareId) {
          return ShareLevel.PUBLIC
        }

        if (shareLookup && dimensionsId in Object.values(shareLookup)) {
          return ShareLevel.VERSIONS
        }

        return ShareLevel.PRIVATE
      },
      MUTA.queryShareLevelError
    ).executeAsync()
  },
  async saveBodyDimensions ({ state, commit, rootGetters }, dimensionsData) {
    const accountId = rootGetters.accountId()
    if (!accountId) { return }

    // TODO This should queue a save. Guard against infinite loop.
    if (dimensionsData.id && state.isSavingDimensions(dimensionsData.id)) { return }

    console.log('Save body dimensions to account', dimensionsData)
  },
  async buildBody ({ state, commit }, dimensionsData) {
    const id = dimensionsData.id
    if (isBlockingRequest(state, id)) { return }

    day6Analytics.buildRequestStart()

    const isQueuingBuild = b => commit(MUTA.isQueuingBuild, b)
    isQueuingBuild(true)
    try {
      const {
        buildFileUri,
        dimensionsId,
        buildStatusId
      } = await day6Api.buildBody(dimensionsData)

      let buildResponseData

      if (buildFileUri) {
        const fileDownloadUrl = await getStorageFileDownloadUrl(buildFileUri)
        buildResponseData = { id, fileDownloadUrl }
      } else if (buildStatusId) {
        buildResponseData = { id, dimensionsId, buildStatusId }
      }

      if (buildResponseData) { return buildResponseData }

      throw new Error(`Unexpected build response for ${id}`)
    } catch (error) {
      return { error }
    } finally {
      isQueuingBuild(false)
    }
  },
  async savePose ({ state, commit }, {
    dimensionsId,
    dimensions,
    poseId,
    pose,
    sequenceId,
    sequenceData,
    animationGroupId
  }) {
    if (isBlockingRequest(state, dimensionsId)) { return }

    // Analytics is not required as this is invoked reactively

    const isSavingPose = b => commit(MUTA.isSavingPose, b)
    isSavingPose(true)
    try {
      const requestData = {
        dimensionsId,
        bodyDimensions: dimensions,
        poseId,
        characterPose: pose,
        poseSequenceId: sequenceId,
        poseSequenceData: sequenceData,
        animationGroupId
      }
      const saveResponse = await day6Api.savePose(requestData)
      let poseResponseData
      if (saveResponse.poseId) {
        poseResponseData = saveResponse
      } else if (saveResponse.notModified) {
        poseResponseData = {
          poseId,
          dimensionsId,
          poseSequenceId: sequenceId,
          animationGroupId
        }
      }

      if (poseResponseData) { return poseResponseData }

      throw new Error(`Unexpected pose response for ${poseId} ${dimensionsId}`)
    } catch (error) {
      return { error }
    } finally {
      isSavingPose(false)
    }
  },
  async savePreviewSettings ({ state, commit }, { dimensionsId, previewSettings }) {
    if (!(dimensionsId && previewSettings)) { return }

    if (state.isSavingPreviewSettings) { return }

    const requestData = {
      dimensionsId,
      settings: previewSettings
    }
    return new NetworkCommand(
      commit,
      MUTA.isSavingPreviewSettings,
      async () => {
        await sceneApi.savePreviewSettings(requestData)
        return true
      }
    ).executeAsync()
  },
  // Shouldn't need to be async but will still be async if not specified so might as well be explicit.
  // Remove the async and call from mapAction to see it is still async...
  async observeStatus (_, { statusId, docObserver }) {
    const docPath = `job-status/${statusId}`
    const docRef = firestoreDoc(docPath)
    return onSnapshot(docRef, docObserver)
  },
  async shareAccountCharacter ({ state, commit, rootGetters }, { dimensionsId, shareLevel }) {
    if (state.isSharingCharacter) { return {} }

    const accountId = rootGetters.accountId()
    const isDocumentExist = async accountPath => {
      const snap = await snapshotBodyDimensions(accountPath, dimensionsId)
      return snap.exists()
    }
    let isAccountDimensions = false
    let isPublicDimensions = false
    const isAccountPaid = accountId && rootGetters.isAccountPaid
    if (isAccountPaid) {
      isAccountDimensions = await isDocumentExist(accountId)
      if (!isAccountDimensions) {
        isPublicDimensions = await isDocumentExist('_')
      }
    } else {
      isPublicDimensions = await isDocumentExist('_')
      if (!isPublicDimensions && accountId) {
        isAccountDimensions = await isDocumentExist(accountId)
      }
    }

    if (isPublicDimensions) {
      return { dimensionsId, shareId: dimensionsId }
    }

    if (!isAccountDimensions) {
      return {}
    }

    const isSharingCharacter = b => commit(MUTA.isSharingCharacter, b)
    isSharingCharacter(true)

    try {
      const requestData = {
        dimensionsId,
        shareLevel
      }
      const { shareId } = await day6Api.shareCharacter(requestData)
      const result = { dimensionsId }
      if (shareLevel === ShareLevel.PRIVATE) {
        // No action required
      } else if (shareId) {
        result.shareId = shareId
      } else {
        throw new Error(`Unexpected share response for ${shareLevel}`)
      }
      return result
    } catch (error) {
      return { error }
    } finally {
      isSharingCharacter(false)
    }
  },
  async querySummaryIdDimensions ({ rootGetters }, idSummary) {
    const accountId = rootGetters.accountId()
    if (!(accountId && rootGetters.isAccountPaid)) { return '' }

    // Not a stateful query
    const summary = await getSummaryData(accountId, idSummary)
    return summary.current
  }
}

// mutations
const mutations = {
  [MUTA.isQueryingDimensions] (s, b) {
    s.isQueryingTimes = b
  },
  [MUTA.queryDimensionsError] (s, e) {
    s.queryingTimesError = e
  },
  [MUTA.setSaveDimensions] (s, { id, isSaving }) {
    if (isSaving) {
      s.saveDimensionIds[id] = true
    } else {
      delete s.saveDimensionIds[id]
    }
  },
  [MUTA.saveDimensionsError] (s, e) {
    s.saveDimensionsError = e
  },
  [MUTA.isQueuingBuild] (s, b) {
    s.isQueuingBuild = b
  },
  [MUTA.isQueryingPose] (s, b) {
    s.isQueryingPose = b
  },
  [MUTA.isSavingPose] (s, b) {
    s.isSavingPose = b
  },
  [MUTA.isQueryingPreviewSettings] (s, b) {
    s.isQueryingPreviewSettings = b
  },
  [MUTA.isSavingPreviewSettings] (s, b) {
    s.isSavingPreviewSettings = b
  },
  [MUTA.isSharingCharacter] (s, b) {
    s.isSharingCharacter = b
  },
  [MUTA.queryingShareId] (s, { id }) {
    s.queryingShareId = id
  },
  [MUTA.queryShareLevelError] (s, e) {
    s.queryShareLevelError = e
  }
}

export default {
  namespaced: true,
  state: initialState,
  getters: moduleGetters,
  actions,
  mutations
}
